import { useQuery } from "@apollo/client/react";
import {
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { HTMLAttributes, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { JSX } from "react/jsx-runtime";
import DraftStatus from "../../constants/draftStatus";
import {
  ChangelistDiffFilesDocument,
  ChangelistDiffFilesQuery,
  ChangelistDiffFilesQueryVariables,
} from "../../graphql/types";
import DiffIcon from "../../icons/Diff.svg?react";
import { AppError } from "../../pages/static/AppError";
import { useEditorStoreContext } from "../../store/editor/EditorStoreProvider";
import { EditorTabVariant, selectErrorsAndWarnings } from "../../store/editor/editorStore";
import { sbTheme } from "../../styles/sbDesignSystem/sbTheme";
import { DataLoader, ErrorContainer, LoadingContainer, SuccessContainer } from "../DataLoader";
import { LoadingIcon } from "../ui/LoadingIcon";

const PanelContainer = styled(Paper)(({ theme }) => ({
  height: "100%",
  overflow: "auto",
  borderRight: `0.05px solid ${theme.palette.surfaceLight50.main}`,
}));

const CollapsibleContainer = ({
  title,
  children,
  depth = 1,
}: {
  title: string;
  children: React.ReactNode;
  depth?: number;
}) => {
  const [open, setOpen] = useState(true);

  return (
    <>
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <Typography
          sx={{
            pr: 2,
            pt: depth == 1 ? 2 : 0,
            pb: 2,
            pl: depth,
            flex: 2,
            fontWeight: depth == 1 ? "bold" : 500,
            fontSize: depth == 1 ? 16 : 14,
          }}
        >
          {title}
        </Typography>
        <IconButton onClick={() => setOpen((prev) => !prev)}>{open ? <CaretUpIcon /> : <CaretDownIcon />}</IconButton>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Stack sx={{ pl: depth }}>{children}</Stack>
      </Collapse>
    </>
  );
};

const FileItemIcon = styled(ListItemIcon)({
  position: "relative",
  "& .compilation-info-icon": {
    position: "absolute",
    bottom: -3,
    right: 12,
  },
  minWidth: "40px",
});

const FileListItem = ({
  filePath,
  selected,
  onClick,
  key,
  deleted,
  type = "file",
  ...props
}: {
  filePath: string;
  selected: boolean;
  onClick: () => void;
  key: number;
  deleted?: boolean;
  type?: "file" | "destination" | "datasource";
} & HTMLAttributes<HTMLElement>) => {
  const theme = useTheme();
  const [errors, warnings, beta] = useEditorStoreContext(selectErrorsAndWarnings(filePath));

  let iconColor: string | undefined = undefined;
  let textColor: string | undefined = undefined;
  let fileName = "";

  if (warnings || beta) {
    iconColor = theme.palette.warning.main;
    textColor = theme.palette.warning.main;
  }
  if (errors) {
    iconColor = theme.palette.error.main;
    textColor = theme.palette.error.main;
  }

  const icon: JSX.Element | null = null;
  if (type === "file") {
    fileName = filePath;
  }

  return (
    <ListItemButton onClick={onClick} key={key} selected={selected} {...props}>
      <FileItemIcon>
        {icon ? (
          <>{icon}</>
        ) : (
          <>
            <DiffIcon fontSize="large" className="list-item-icon" color={iconColor} width="25px" height="25px" />
            {iconColor ? (
              <WarningIcon
                size={11}
                color={iconColor}
                className="compilation-info-icon"
                data-testid={`${errors ? "error" : "warning"}-icon`}
              />
            ) : null}
          </>
        )}
      </FileItemIcon>
      <ListItemText
        primary={fileName}
        slotProps={{
          primary: {
            color: deleted ? sbTheme.palette.surfaceDark10.main : textColor,
            sx: {
              textDecoration: deleted ? "line-through" : undefined,
            },
          },
        }}
      />
    </ListItemButton>
  );
};

const DiffFilePanel = ({ originalId, modifiedId }: { originalId?: string; modifiedId: string }) => {
  const openDiffTab = useEditorStoreContext(
    (state) => (file: string, type: "file") => state.openFileTab(file, type, file, EditorTabVariant.EditableDiff)
  );
  const selectedFile = useEditorStoreContext((state) => state.currentOpenFile?.path);
  const readOnly = useEditorStoreContext((state) => [DraftStatus.RUNNING, DraftStatus.REPLACED].includes(state.status));

  const handleFileSelected = (file: string, type: "file") => {
    openDiffTab(file, type);
    const currentParams = Object.fromEntries([...searchParams]);
    setSearchParams({
      ...currentParams,
      variant: readOnly ? EditorTabVariant.ReadonlyDiff : EditorTabVariant.EditableDiff,
      file: encodeURIComponent(file),
      fileType: type,
    });
  };

  const { loading, error, data } = useQuery<ChangelistDiffFilesQuery, ChangelistDiffFilesQueryVariables>(
    ChangelistDiffFilesDocument,
    {
      variables: {
        original_id: originalId,
        modified_id: modifiedId,
      },
      fetchPolicy: "network-only",
    }
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const modifiedFiles = data?.changelist_diff?.modified_files;
  const createdFiles = data?.changelist_diff?.created_files;
  const deletedFiles = data?.changelist_diff?.deleted_files;
  const unchangedFiles = data?.changelist_diff?.unchanged_files;
  const hasAnyFile = modifiedFiles?.length || createdFiles?.length || deletedFiles?.length || unchangedFiles?.length;

  return (
    <PanelContainer>
      <DataLoader loading={loading} error={error} success={Boolean(data)}>
        <LoadingContainer>
          <LoadingIcon />
        </LoadingContainer>
        <ErrorContainer>
          <AppError />
        </ErrorContainer>
        <SuccessContainer>
          {hasAnyFile ? (
            <CollapsibleContainer title="Files">
              {modifiedFiles?.length ? (
                <CollapsibleContainer title="Changed Files" depth={2}>
                  <List data-cy="changed-files-list" disablePadding>
                    {modifiedFiles.map((file, idx) => (
                      <FileListItem
                        filePath={file}
                        selected={selectedFile === file}
                        onClick={() => handleFileSelected(file, "file")}
                        data-testid="changed-file-item"
                        key={idx}
                      />
                    ))}
                  </List>
                </CollapsibleContainer>
              ) : null}
              {createdFiles?.length ? (
                <CollapsibleContainer title="Created Files" depth={2}>
                  <List data-cy="created-files-list" disablePadding>
                    {createdFiles.map((file, idx) => (
                      <FileListItem
                        filePath={file}
                        selected={selectedFile === file}
                        onClick={() => handleFileSelected(file, "file")}
                        data-testid="created-file-item"
                        key={idx}
                      />
                    ))}
                  </List>
                </CollapsibleContainer>
              ) : null}
              {deletedFiles?.length ? (
                <CollapsibleContainer title="Deleted Files" depth={2}>
                  <List data-cy="deleted-files-list" disablePadding>
                    {deletedFiles.map((file, idx) => (
                      <FileListItem
                        filePath={file}
                        selected={selectedFile === file}
                        onClick={() => handleFileSelected(file, "file")}
                        data-testid="deleted-file-item"
                        key={idx}
                        deleted
                      />
                    ))}
                  </List>
                </CollapsibleContainer>
              ) : null}
              {unchangedFiles?.length ? (
                <CollapsibleContainer title="Unchanged Files" depth={2}>
                  <List data-cy="unchanged-files-list" disablePadding>
                    {unchangedFiles.map((file, idx) => (
                      <FileListItem
                        filePath={file}
                        selected={selectedFile === file}
                        onClick={() => handleFileSelected(file, "file")}
                        data-testid="unchanged-file-item"
                        key={idx}
                      />
                    ))}
                  </List>
                </CollapsibleContainer>
              ) : null}
            </CollapsibleContainer>
          ) : null}
        </SuccessContainer>
      </DataLoader>
    </PanelContainer>
  );
};

export default DiffFilePanel;
